import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useNavigate, useParams } from 'react-router-dom';
import { FaPlus, FaTrash, FaImage, FaSave, FaArrowLeft } from 'react-icons/fa';
import { getTeacherClasses } from '../../redux/slices/lessonsSlice';
import { getTeacherSubjects } from '../../redux/slices/subjectsSlice';
import { fetchSubjectQuestionBank } from '../../redux/slices/questionBankSlice';
import { createTest } from '../../redux/slices/teacherTestsSlice';
import { toast } from 'react-toastify';
import './CreateTest.css';

function CreateTest() {
    const { isDarkMode } = useDarkMode();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.auth);
    const { teacherClasses } = useSelector((state) => state.lessons);
    const { teacherSubjects, subjectsLoading } = useSelector((state) => state.subjects);
    const { questionBank, loading: questionBankLoading } = useSelector((state) => state.questionBank);
    const { loading: createTestLoading } = useSelector((state) => state.teacherTests);
    
    const [activeTab, setActiveTab] = useState('addQuestions');
    const [selectedClassId, setSelectedClassId] = useState('');
    const [selectedSubjectId, setSelectedSubjectId] = useState('');
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    // Test basic info
    const [testInfo, setTestInfo] = useState({
        title: '',
        day: '',
        start_time: '',
        duration_time: '',
        class_id: '',
        subject_id: ''
    });
    
    // Added questions (new questions)
    const [addedQuestions, setAddedQuestions] = useState([
        {
            text: '',
            image: null,
            answers: [
                { text: '', image: null, true_status: false },
                { text: '', image: null, true_status: false },
                { text: '', image: null, true_status: false },
                { text: '', image: null, true_status: false }
            ]
        }
    ]);
    
    // Selected questions from bank
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    
    // Available subjects for selected class
    const [availableSubjects, setAvailableSubjects] = useState([]);
    
    // Default question degree (fixed value)
    const DEFAULT_QUESTION_DEGREE = 1;
    
    // Submit test
    const handleSubmit = async () => {
        // Validate basic info
        if (!testInfo.title || !testInfo.day || !testInfo.start_time || !testInfo.duration_time || !testInfo.class_id || !testInfo.subject_id) {
            toast.error('يرجى ملء جميع البيانات الأساسية للاختبار');
            return;
        }
        
        // Check if there are questions
        const hasAddedQuestions = addedQuestions.some(q => q.text.trim() && q.answers.some(a => a.text.trim() && a.true_status));
        const hasSelectedQuestions = selectedQuestions.length > 0;
        
        if (!hasAddedQuestions && !hasSelectedQuestions) {
            toast.error('يرجى إضافة أسئلة أو اختيار أسئلة من بنك الأسئلة');
            return;
        }
        
        try {
            let requestData = {
                ...testInfo,
                class_id: selectedClassId,
                subject_id: selectedSubjectId
            };
            
            // Convert duration_time from minutes to "MM:SS" format
            if (requestData.duration_time) {
                const minutes = parseInt(requestData.duration_time);
                const hours = Math.floor(minutes / 60);
                const remainingMinutes = minutes % 60;
                
                // Format as "MM:SS" where MM is total minutes
                const totalMinutes = (hours * 60) + remainingMinutes;
                requestData.duration_time = `${totalMinutes.toString().padStart(2, '0')}:00`;
            }
            
            // Calculate total questions count
            let totalQuestions = 0;
            if (hasAddedQuestions) totalQuestions += addedQuestions.filter(q => q.text.trim() && q.answers.some(a => a.text.trim() && a.true_status)).length;
            if (hasSelectedQuestions) totalQuestions += selectedQuestions.length;
            
            if (activeTab === 'addQuestions' && hasAddedQuestions) {
                // Submit with added questions
                const addedQuestionsData = addedQuestions
                    .filter(q => q.text.trim() && q.answers.some(a => a.text.trim() && a.true_status))
                    .map(q => ({
                        text: q.text,
                        image: q.image,
                        question_degree: DEFAULT_QUESTION_DEGREE, // Fixed default degree
                        answers: q.answers.map(a => ({
                            text: a.text,
                            image: a.image,
                            true_status: a.true_status
                        }))
                    }));
                
                requestData.added_questions = addedQuestionsData;
            } else if (activeTab === 'questionBank' && hasSelectedQuestions) {
                // Submit with selected questions
                const selectedQuestionsData = selectedQuestions.map(q => ({
                    ...q,
                    question_degree: DEFAULT_QUESTION_DEGREE // Fixed default degree
                }));
                
                requestData.questions = selectedQuestionsData;
            }
            
            // Handle case where both types of questions exist
            if (hasAddedQuestions && hasSelectedQuestions) {
                // Submit with both added and selected questions
                const bothAddedQuestionsData = addedQuestions
                    .filter(q => q.text.trim() && q.answers.some(a => a.text.trim() && a.true_status))
                    .map(q => ({
                        text: q.text,
                        image: q.image,
                        question_degree: DEFAULT_QUESTION_DEGREE, // Fixed default degree
                        answers: q.answers.map(a => ({
                            text: a.text,
                            image: a.image,
                            true_status: a.true_status
                        }))
                    }));
                
                const bothSelectedQuestionsData = selectedQuestions.map(q => ({
                    ...q,
                    question_degree: DEFAULT_QUESTION_DEGREE // Fixed default degree
                }));
                
                requestData.added_questions = bothAddedQuestionsData;
                requestData.questions = bothSelectedQuestionsData;
            }
            
            await dispatch(createTest(requestData)).unwrap();
            
            // Show success message with question count
            const totalQuestionsCount = (requestData.added_questions?.length || 0) + (requestData.questions?.length || 0);
            toast.success(`تم إنشاء الاختبار بنجاح! عدد الأسئلة: ${totalQuestionsCount} سؤال`);
            
            navigate('/exams');
        } catch (error) {
            console.error('Error creating test:', error);
            const errorMessage = error.message || 'حدث خطأ في إنشاء الاختبار';
            toast.error(errorMessage);
        }
    };
    
    useEffect(() => {
        // Check if user is logged in from localStorage
        const token = localStorage.getItem('token3almny');
        const userRoleString = localStorage.getItem('user3almny');
        
        // Parse userRole if it's a JSON string
        let userRole;
        try {
            userRole = userRoleString ? JSON.parse(userRoleString) : null;
        } catch (e) {
            userRole = null;
        }
        
        if (!token || !userRole || userRole.role !== 'teacher') {
            navigate('/login');
            return;
        }
        
        // If we have token but no currentUser, wait for Redux to load
        if (token && userRole.role === 'teacher' && !currentUser) {
            return;
        }
        
        // If we have both token and currentUser, proceed
        if (token && userRole.role === 'teacher' && currentUser) {
            dispatch(getTeacherClasses());
            dispatch(getTeacherSubjects());
        }
    }, [dispatch, currentUser, navigate]);
    
    // Watch for currentUser changes and dispatch getTeacherClasses and getTeacherSubjects when available
    useEffect(() => {
        if (currentUser && currentUser.role === 'teacher') {
            dispatch(getTeacherClasses());
            dispatch(getTeacherSubjects());
        }
    }, [currentUser, dispatch]);
    
    // Debug teacherSubjects loading
    useEffect(() => {
        if (teacherSubjects && teacherSubjects.length > 0) {
            // Set available subjects when teacherSubjects loads
            setAvailableSubjects(teacherSubjects);
        }
    }, [teacherSubjects]);
    
    // Debug teacherClasses loading
    useEffect(() => {
        if (teacherClasses && teacherClasses.length > 0) {
            console.log('First class:', teacherClasses[0]);
            console.log('First class subjects:', teacherClasses[0].subjects);
        }
    }, [teacherClasses]);
    
    // When class changes, update subjects
    useEffect(() => {
        if (selectedClassId && teacherClasses.length > 0) {
            const selectedClass = teacherClasses.find(c => c.id == selectedClassId);
            
            if (selectedClass && selectedClass.subjects) {
                setAvailableSubjects(selectedClass.subjects);
                if (selectedClass.subjects.length > 0) {
                    const firstSubjectId = selectedClass.subjects[0].id;
                    setSelectedSubjectId(firstSubjectId);
                    
                    // If we're currently on question bank tab, fetch questions immediately
                    if (activeTab === 'questionBank') {
                        dispatch(fetchSubjectQuestionBank(firstSubjectId));
                    }
                }
            }
            
            // Update testInfo with class_id
            setTestInfo(prev => ({
                ...prev,
                class_id: selectedClassId
            }));
        } else if (teacherSubjects && teacherSubjects.length > 0) {
            // If no class selected but we have subjects, use all subjects
            setAvailableSubjects(teacherSubjects);
        }
    }, [selectedClassId, teacherClasses, teacherSubjects, activeTab, dispatch]);
    
    // When availableSubjects change, auto-select first subject if none selected
    useEffect(() => {
        if (availableSubjects && availableSubjects.length > 0 && !selectedSubjectId) {
            const firstSubjectId = availableSubjects[0].id;
            setSelectedSubjectId(firstSubjectId);
        }
    }, [availableSubjects, selectedSubjectId]);
    
    // When subject changes, update testInfo
    useEffect(() => {
        if (selectedSubjectId) {
            setTestInfo(prev => ({
                ...prev,
                subject_id: selectedSubjectId
            }));
        }
    }, [selectedSubjectId]);
    
    // Fetch question bank when subject changes or tab changes
    useEffect(() => {
        if (selectedSubjectId && activeTab === 'questionBank') {
            dispatch(fetchSubjectQuestionBank(selectedSubjectId));
        }
    }, [selectedSubjectId, activeTab, dispatch]);
    
    // Also fetch when switching to question bank tab if subject is already selected
    useEffect(() => {
        if (activeTab === 'questionBank' && selectedSubjectId) {
            dispatch(fetchSubjectQuestionBank(selectedSubjectId));
        } else if (activeTab === 'questionBank' && !selectedSubjectId && teacherSubjects && teacherSubjects.length > 0) {
            // If switching to question bank tab but no subject selected, use first available subject
            const firstSubject = teacherSubjects[0];
            setSelectedSubjectId(firstSubject.id);
            dispatch(fetchSubjectQuestionBank(firstSubject.id));
        }
    }, [activeTab, selectedSubjectId, teacherSubjects, dispatch]);
    
    // Handle test info change
    const handleTestInfoChange = (field, value) => {
        setTestInfo(prev => ({
            ...prev,
            [field]: value
        }));
    };
    
    // Add new question
    const addQuestion = () => {
        setAddedQuestions(prev => [...prev, {
            text: '',
            image: null,
            answers: [
                { text: '', image: null, true_status: false },
                { text: '', image: null, true_status: false },
                { text: '', image: null, true_status: false },
                { text: '', image: null, true_status: false }
            ]
        }]);
    };
    
    // Remove question
    const removeQuestion = (index) => {
        setAddedQuestions(prev => prev.filter((_, i) => i !== index));
    };
    
    // Handle question text change
    const handleQuestionTextChange = (questionIndex, text) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === questionIndex ? { ...q, text } : q
        ));
    };
    
    // Handle question image change
    const handleQuestionImageChange = (questionIndex, file) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === questionIndex ? { ...q, image: file } : q
        ));
    };
    
    // Add answer to question
    const addAnswer = (questionIndex) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === questionIndex ? {
                ...q,
                answers: [...q.answers, { text: '', image: null, true_status: false }]
            } : q
        ));
    };
    
    // Remove answer from question
    const removeAnswer = (questionIndex, answerIndex) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === questionIndex ? {
                ...q,
                answers: q.answers.filter((_, ai) => ai !== answerIndex)
            } : q
        ));
    };
    
    // Handle answer text change
    const handleAnswerTextChange = (questionIndex, answerIndex, text) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === questionIndex ? {
                ...q,
                answers: q.answers.map((a, ai) => 
                    ai === answerIndex ? { ...a, text } : a
                )
            } : q
        ));
    };
    
    // Handle answer image change
    const handleAnswerImageChange = (questionIndex, answerIndex, file) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === questionIndex ? {
                ...q,
                answers: q.answers.map((a, ai) => 
                    ai === answerIndex ? { ...a, image: file } : a
                )
            } : q
        ));
    };
    
    // Handle correct answer selection
    const handleCorrectAnswerChange = (questionIndex, answerIndex) => {
        setAddedQuestions(prev => prev.map((q, i) => 
            i === questionIndex ? {
                ...q,
                answers: q.answers.map((a, ai) => ({
                    ...a,
                    true_status: ai === answerIndex
                }))
            } : q
        ));
    };
    
    // Handle question selection from bank
    const handleQuestionSelection = (questionId, questionDegree) => {
        
        const existingIndex = selectedQuestions.findIndex(q => q.question_id === questionId);
        
        if (existingIndex >= 0) {
            // Remove if already selected
            setSelectedQuestions(prev => prev.filter((_, i) => i !== existingIndex));
        } else {
            // Add new selection
            setSelectedQuestions(prev => [...prev, { question_id: questionId, question_degree: questionDegree }]);
        }
    };
    
    // Get letter for answer (أ، ب، ج، د)
    const getAnswerLetter = (index) => {
        const letters = ['أ', 'ب', 'ج', 'د', 'ه', 'و', 'ز', 'ح', 'ط', 'ي'];
        return letters[index] || String.fromCharCode(65 + index);
    };
    
    // Handle tab change
    const handleTabChange = (tabName) => {
        
        setActiveTab(tabName);
        
        // If switching to question bank
        if (tabName === 'questionBank') {
            if (selectedSubjectId) {
                dispatch(fetchSubjectQuestionBank(selectedSubjectId));
            } else if (selectedClassId && availableSubjects.length > 0) {
                // If we have a class but no subject, use the first available subject
                const firstSubject = availableSubjects[0];
                setSelectedSubjectId(firstSubject.id);
                dispatch(fetchSubjectQuestionBank(firstSubject.id));
            } else if (teacherSubjects && teacherSubjects.length > 0) {
                // If no class selected but we have subjects, use the first subject
                const firstSubject = teacherSubjects[0];
                setSelectedSubjectId(firstSubject.id);
                dispatch(fetchSubjectQuestionBank(firstSubject.id));
            } else {
                toast.warning('يرجى اختيار الصف أولاً');
            }
        }
    };
    
    if (!currentUser || currentUser.role !== 'teacher') {
        return null;
    }
    
    return (
        <div className={`create-test-section ${isDarkMode ? "dark-mode" : "light-mode"}`}>
            <div className="container">
                {/* Header */}
                <div className="create-test-header">
                    <button 
                        className="back-btn"
                        onClick={() => navigate('/exams')}
                    >
                        <FaArrowLeft />
                        رجوع
                    </button>
                    <h3 style={{marginTop : "-10px"}}>إنشاء اختبار جديد</h3>
                </div>
                
                {/* Basic Test Info */}
                <div className="test-info-section">
                    <h2>معلومات الاختبار الأساسية</h2>
                    <div className="test-info-grid">
                        <div className="form-group">
                            <label>عنوان الاختبار</label>
                            <input
                                type="text"
                                value={testInfo.title}
                                onChange={(e) => handleTestInfoChange('title', e.target.value)}
                                placeholder="أدخل عنوان الاختبار"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>الصف</label>
                            <select
                                value={selectedClassId}
                                onChange={(e) => setSelectedClassId(e.target.value)}
                            >
                                <option value="">اختر الصف</option>
                                {teacherClasses.map(cls => (
                                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label>المادة</label>
                            <select
                                value={selectedSubjectId}
                                onChange={(e) => setSelectedSubjectId(e.target.value)}
                                disabled={!selectedClassId && (!availableSubjects || availableSubjects.length === 0)}
                            >
                                <option value="">اختر المادة</option>
                                {availableSubjects.map(subject => (
                                    <option key={subject.id} value={subject.id}>{subject.title}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label>التاريخ</label>
                            <input
                                type="date"
                                value={testInfo.day}
                                onChange={(e) => handleTestInfoChange('day', e.target.value)}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>وقت البدء</label>
                            <input
                                type="time"
                                value={testInfo.start_time}
                                onChange={(e) => handleTestInfoChange('start_time', e.target.value)}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>مدة الاختبار (بالدقائق)</label>
                            <input
                                type="number"
                                value={testInfo.duration_time}
                                onChange={(e) => handleTestInfoChange('duration_time', e.target.value)}
                                placeholder="مثال: 60 (ساعة واحدة)"
                                min="1"
                            />
                            <small className="form-help">سيتم تحويل الدقائق تلقائياً إلى الشكل المطلوب (مثال: 60 دقيقة = 60:00)</small>
                        </div>
                    </div>
                </div>
                
                {/* Tabs */}
                <div className="create-test-tabs">
                    <div className="tabs-header">
                        <button
                            className={`tab-button ${activeTab === 'addQuestions' ? 'active' : ''}`}
                            onClick={() => handleTabChange('addQuestions')}
                        >
                            إضافة أسئلة جديدة
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'questionBank' ? 'active' : ''}`}
                            onClick={() => handleTabChange('questionBank')}
                        >
                            اختيار من بنك الأسئلة
                        </button>
                    </div>
                    
                    {/* Tab Content */}
                    <div className="tab-content">
                        {activeTab === 'addQuestions' ? (
                            /* Add Questions Tab */
                            <div className="add-questions-tab" style={{padding : "10px"}}>
                                <div className="questions-header">
                                    <h3>الأسئلة المضافة</h3>
                                    <button className="add-question-btn" onClick={addQuestion}>
                                        <FaPlus />
                                        إضافة سؤال
                                    </button>
                                </div>
                                
                                {addedQuestions.map((question, questionIndex) => (
                                    <div key={questionIndex} className="question-card">
                                        <div className="question-header">
                                            <h4>السؤال {questionIndex + 1}</h4>
                                            <button 
                                                className="remove-question-btn"
                                                onClick={() => removeQuestion(questionIndex)}
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                        
                                        <div className="question-content">
                                            <div className="form-group">
                                                <label>نص السؤال</label>
                                                <textarea
                                                    value={question.text}
                                                    onChange={(e) => handleQuestionTextChange(questionIndex, e.target.value)}
                                                    placeholder="أدخل نص السؤال"
                                                    rows="3"
                                                />
                                            </div>
                                            
                                            <div className="form-group">
                                                <label>صورة السؤال (اختياري)</label>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => handleQuestionImageChange(questionIndex, e.target.files[0])}
                                                />
                                                {question.image && (
                                                    <div className="image-preview">
                                                        <img src={URL.createObjectURL(question.image)} alt="Question" />
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="answers-section">
                                                <div className="answers-header">
                                                    <h5>الإجابات</h5>
                                                    <button 
                                                        className="add-answer-btn"
                                                        onClick={() => addAnswer(questionIndex)}
                                                    >
                                                        <FaPlus />
                                                        إضافة إجابة
                                                    </button>
                                                </div>
                                                
                                                {question.answers.map((answer, answerIndex) => (
                                                    <div key={answerIndex} className="answer-item">
                                                        <div className="answer-content">
                                                            <div className="answer-letter">
                                                                {getAnswerLetter(answerIndex)}
                                                            </div>
                                                            
                                                            <div className="answer-inputs">
                                                                <input
                                                                    type="text"
                                                                    value={answer.text}
                                                                    onChange={(e) => handleAnswerTextChange(questionIndex, answerIndex, e.target.value)}
                                                                    placeholder="نص الإجابة"
                                                                />
                                                                
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    onChange={(e) => handleAnswerImageChange(questionIndex, answerIndex, e.target.files[0])}
                                                                    placeholder="صورة الإجابة"
                                                                />
                                                                
                                                                {answer.image && (
                                                                    <div className="image-preview small">
                                                                        <img src={URL.createObjectURL(answer.image)} alt="Answer" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            
                                                            <div className="answer-actions">
                                                                <label className="correct-answer">
                                                                    <input
                                                                        type="radio"
                                                                        name={`correct-${questionIndex}`}
                                                                        checked={answer.true_status}
                                                                        onChange={() => handleCorrectAnswerChange(questionIndex, answerIndex)}
                                                                    />
                                                                    الإجابة الصحيحة
                                                                </label>
                                                                
                                                                {question.answers.length > 2 && (
                                                                    <button 
                                                                        className="remove-answer-btn"
                                                                        onClick={() => removeAnswer(questionIndex, answerIndex)}
                                                                    >
                                                                        <FaTrash />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            /* Question Bank Tab */
                            <div className="question-bank-tab" style={{padding : "30px"}}>
                                <div className="question-bank-header">
                                    <h3>بنك الأسئلة - {availableSubjects.find(s => s.id == selectedSubjectId)?.title}</h3>
                                    <p>اختر الأسئلة التي تريد إضافتها للاختبار</p>
                                </div>
                                
                                {questionBankLoading ? (
                                    <div className="loading">جاري تحميل الأسئلة...</div>
                                ) : (
                                    <div className="question-bank-grid">
                                        {questionBank.map((question) => (
                                            <div 
                                                key={question.id} 
                                                className={`question-bank-card ${selectedQuestions.find(q => q.question_id === question.id) ? 'selected' : ''}`}
                                                onClick={() => handleQuestionSelection(question.id, DEFAULT_QUESTION_DEGREE)}
                                            >
                                                <div className="question-bank-content">
                                                    <h4>{question.title}</h4>
                                                    <div className="question-meta">
                                                        <span>عدد الإجابات: {question.answers?.length || 0}</span>
                                                        <span>النوع: سؤال اختيار من متعدد</span>
                                                    </div>
                                                    
                                                    {/* Show answers preview */}
                                                    {question.answers && question.answers.length > 0 && (
                                                        <div className="answers-preview">
                                                            <h5>الإجابات:</h5>
                                                            <div className="answers-list">
                                                                {question.answers.map((answer, index) => (
                                                                    <div 
                                                                        key={answer.id} 
                                                                        className={`answer-preview ${answer.true_status ? 'correct' : ''}`}
                                                                    >
                                                                        <span className="answer-letter">{String.fromCharCode(65 + index)}</span>
                                                                        <span className="answer-text">{answer.answer}</span>
                                                                        {answer.true_status && <span className="correct-mark">✓</span>}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                <div className="question-selection">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedQuestions.find(q => q.question_id === question.id) ? true : false}
                                                        onChange={() => handleQuestionSelection(question.id, DEFAULT_QUESTION_DEGREE)}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Submit Button */}
                <div className="submit-section">
                    <button 
                        className="submit-btn mx-auto"
                        onClick={handleSubmit}
                        disabled={!selectedClassId || !selectedSubjectId || createTestLoading}
                    >
                        {createTestLoading ? (
                            <>
                                <div className="loading-spinner"></div>
                                جاري الإنشاء...
                            </> 
                        ) : (
                            <>
                                <FaSave />
                                إنشاء الاختبار
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CreateTest;
